/**
 * Stock from the journal (docs/04-domaenenmodell.md, "Bestandsberechnung" and "Bewertung").
 *
 * - The journal is processed in canonical order (time, then id), so every device gets the same
 *   result, whatever order the bookings arrived in.
 * - A count stores the counted quantity; the difference is derived here, every time. A late
 *   booking before the count changes the difference, not the stock after the count.
 * - A cancelled booking counts as never booked (retroactively), and so does its cancel.
 * - Lot articles: issues without a lot take first what expires first (FEFO: best-before date,
 *   docs/18-vorratsbuch.md), lots without a date last; among equals the oldest (receipt time, then id).
 *   What is taken beyond the lots is a deficit; the next receipt at that location settles it.
 * - Valuation: lot articles exactly per lot, quantity articles at the moving average per article
 *   and storage (F9).
 * - A snapshot (Stichtagsstand) holds the state at a cutoff; snapshot + later bookings gives the
 *   same result as the whole journal.
 */
import type { Booking, Count, CountMode, Id, LotInfo, Millis, Receipt, Tracking } from "./model.ts";
import { compareBookings } from "./model.ts";

export interface Lot extends LotInfo {
  id: Id;
  article: Id;
  /** Time of the receipt that created it; orders lots for "oldest first". */
  receivedAt: Millis;
  /** Cents per base unit; null when unknown (gift, found in a count, provisional transfer). */
  unitPrice: number | null;
}

/** Stock account: article × location (× lot for lot articles; "" for quantity articles). */
export interface Account {
  article: Id;
  lager: Id;
  location: Id;
  lot: Id;
  qty: number;
}

/** Taken beyond the lots at a location (negative stock of a lot article). */
export interface Deficit {
  article: Id;
  lager: Id;
  location: Id;
  qty: number;
}

/** Moving average of a quantity article in a storage. */
export interface Average {
  qty: number;
  /** Cents per base unit; null until a priced receipt arrived. */
  price: number | null;
}

/** One part of a quantity that moved: from which lot, at which price. */
export interface Part {
  lot: Id;
  qty: number;
  unitPrice: number | null;
}

/** What an issue, transfer, conversion or count difference was worth. */
/** A valuation of a quantity article has exactly one part without lot. */
function isQuantityValuation(val: Valuation): boolean {
  return val.parts.length === 1 && val.parts[0]!.lot === "";
}

export interface Valuation {
  value: number;
  parts: Part[];
  /** Quantity without a known price (valued at 0). */
  unpriced: number;
  /** Quantity taken beyond the stock (negative stock). */
  short: number;
}

export interface CountResult {
  id: Id;
  article: Id;
  lager: Id;
  location: Id;
  lot?: Id;
  at: Millis;
  inventory?: Id;
  /** Stock right before the count, from the journal. */
  expected: number;
  counted: number;
  /** counted − expected; negative: shrinkage plus known causes booked separately. */
  diff: number;
  /** Value of the difference in cents. */
  value: number;
}

/**
 * For a transfer into the device's scope from a storage outside of it: which lots arrived at which
 * price. The server computes it from the full journal; without it the transfer arrives as one
 * provisional lot without price.
 */
export interface Resolution {
  parts: { lot: Lot | null; qty: number; unitPrice: number | null }[];
}

export interface ArticleInfo {
  tracking: Tracking;
  /** Overrides the count mode of the storage (e.g. a medicine counted per lot). */
  countMode?: CountMode;
}

export interface ComputeOptions {
  /** Tracking of an article; unknown articles are quantity articles. */
  article?: (id: Id) => ArticleInfo | undefined;
  /** Count mode of a storage, set by its profile (F8); default "article". */
  countMode?: (lager: Id) => CountMode | undefined;
  /** Storages this device holds; bookings outside are ignored. Default: all. */
  scope?: ReadonlySet<Id>;
  resolutions?: ReadonlyMap<Id, Resolution>;
  /** Start from this snapshot; only bookings after its cutoff are processed. */
  from?: Snapshot;
  /** Bookings are already in canonical order (saves the sort). */
  sorted?: boolean;
}

export interface Snapshot {
  cutoff: Millis;
  accounts: Account[];
  lots: Lot[];
  deficits: Deficit[];
  averages: { article: Id; lager: Id; qty: number; price: number | null }[];
  /** Most recent lot per article and location (for a positive count difference). */
  latest: { article: Id; location: Id; lot: Id }[];
}

/** Lot accounts of an article at a location, oldest first; all before `head` are empty. */
export interface Stack {
  accounts: Account[];
  head: number;
}

export interface State {
  cutoff: Millis | null;
  accounts: Map<string, Account>;
  lots: Map<Id, Lot>;
  /** Per article|location: the lot accounts in "oldest first" order (may hold empty ones). */
  stacks: Map<string, Stack>;
  deficits: Map<string, Deficit>;
  averages: Map<string, Average>;
  latest: Map<string, Id>;
  counts: Map<Id, CountResult>;
  valuations: Map<Id, Valuation>;
  /** Bookings ignored because a cancel refers to them. */
  cancelled: Set<Id>;
  /** The last booking processed (canonical order); later ones can be appended with extendState. */
  last: Booking | null;
}

const key2 = (a: string, b: string) => `${a}|${b}`;

/** Cents per base unit of a receipt, from its amount; null without one (gift, opening stock without price). */
export const priceOf = (b: Receipt) => (b.amount !== undefined && b.qty > 0 ? b.amount / b.qty : null);
const key3 = (a: string, b: string, c: string) => `${a}|${b}|${c}`;

function emptyState(): State {
  return {
    cutoff: null,
    accounts: new Map(),
    lots: new Map(),
    stacks: new Map(),
    deficits: new Map(),
    averages: new Map(),
    latest: new Map(),
    counts: new Map(),
    valuations: new Map(),
    cancelled: new Set(),
    last: null,
  };
}

/** The day a lot expires, comparable as text; lots without a date sort after every date. */
const expiresOn = (lot: Lot) => (lot.bestBefore && /^\d{4}-\d{2}-\d{2}/.test(lot.bestBefore) ? lot.bestBefore.slice(0, 10) : "~");

/** Whether lot a goes out before lot b: what expires first, then the oldest (FEFO, then FIFO). */
function olderLot(a: Lot, b: Lot): boolean {
  const x = expiresOn(a);
  const y = expiresOn(b);
  if (x !== y) return x < y;
  return a.receivedAt < b.receivedAt || (a.receivedAt === b.receivedAt && a.id < b.id);
}

class Ledger {
  readonly state: State;
  private readonly opts: ComputeOptions;

  constructor(opts: ComputeOptions, state?: State) {
    this.opts = opts;
    this.state = state ?? (opts.from ? fromSnapshot(opts.from) : emptyState());
  }

  tracking(article: Id): Tracking {
    return this.opts.article?.(article)?.tracking ?? "quantity";
  }

  countMode(article: Id, lager: Id): CountMode {
    return this.opts.article?.(article)?.countMode ?? this.opts.countMode?.(lager) ?? "article";
  }

  inScope(lager: Id): boolean {
    return !this.opts.scope || this.opts.scope.has(lager);
  }

  // ---- accounts and lots ----

  account(article: Id, lager: Id, location: Id, lot: Id): Account {
    const key = key3(article, location, lot);
    let acc = this.state.accounts.get(key);
    if (!acc) {
      acc = { article, lager, location, lot, qty: 0 };
      this.state.accounts.set(key, acc);
    }
    return acc;
  }

  stack(article: Id, location: Id): Stack {
    const key = key2(article, location);
    let stack = this.state.stacks.get(key);
    if (!stack) {
      stack = { accounts: [], head: 0 };
      this.state.stacks.set(key, stack);
    }
    return stack;
  }

  /** Adds qty to a lot at a location, making the lot known there (in "oldest first" order). */
  putIn(lot: Lot, lager: Id, location: Id, qty: number): Account {
    const key = key3(lot.article, location, lot.id);
    const stack = this.stack(lot.article, location);
    let acc = this.state.accounts.get(key);
    if (acc) {
      // An older account may get stock again: search from the start next time.
      if (qty > 0) stack.head = 0;
    } else {
      acc = this.account(lot.article, lager, location, lot.id);
      // Lots mostly arrive in time order: search from the end.
      let i = stack.accounts.length;
      while (i > 0 && olderLot(lot, this.state.lots.get(stack.accounts[i - 1]!.lot)!)) i--;
      stack.accounts.splice(i, 0, acc);
      if (i < stack.head) stack.head = i;
      const stackKey = key2(lot.article, location);
      const latest = this.state.latest.get(stackKey);
      if (!latest || olderLot(this.state.lots.get(latest)!, lot)) this.state.latest.set(stackKey, lot.id);
    }
    acc.qty += qty;
    return acc;
  }

  addLot(lot: Lot): Lot {
    const known = this.state.lots.get(lot.id);
    if (known) return known;
    this.state.lots.set(lot.id, lot);
    return lot;
  }

  deficit(article: Id, lager: Id, location: Id): Deficit {
    const key = key2(article, location);
    let d = this.state.deficits.get(key);
    if (!d) {
      d = { article, lager, location, qty: 0 };
      this.state.deficits.set(key, d);
    }
    return d;
  }

  /** Adds to a lot at a location; an open deficit there is settled first. */
  putLot(lot: Lot, lager: Id, location: Id, qty: number) {
    const def = this.state.deficits.get(key2(lot.article, location));
    if (def && def.qty > 0) {
      const settle = Math.min(def.qty, qty);
      def.qty -= settle;
      qty -= settle;
    }
    this.putIn(lot, lager, location, qty);
  }

  /** Takes qty from the lots at a location, oldest first (or from one lot); the rest becomes a deficit. */
  takeLots(article: Id, lager: Id, location: Id, qty: number, lotId?: Id): Valuation {
    const val: Valuation = { value: 0, parts: [], unpriced: 0, short: 0 };
    const take = (acc: Account | undefined) => {
      if (!acc || acc.qty <= 0 || qty <= 0) return;
      const n = Math.min(acc.qty, qty);
      acc.qty -= n;
      qty -= n;
      const lot = this.state.lots.get(acc.lot)!;
      val.parts.push({ lot: acc.lot, qty: n, unitPrice: lot.unitPrice });
      if (lot.unitPrice === null) val.unpriced += n;
      else val.value += n * lot.unitPrice;
    };
    if (lotId) take(this.state.accounts.get(key3(article, location, lotId)));
    else {
      const stack = this.state.stacks.get(key2(article, location));
      if (stack) {
        const list = stack.accounts;
        for (let i = stack.head; i < list.length && qty > 0; i++) take(list[i]);
        while (stack.head < list.length && list[stack.head]!.qty <= 0) stack.head++;
      }
    }
    if (qty > 0) {
      this.deficit(article, lager, location).qty += qty;
      val.short = qty;
    }
    return val;
  }

  lotQty(article: Id, location: Id): number {
    let sum = 0;
    const stack = this.state.stacks.get(key2(article, location));
    if (stack) for (let i = stack.head; i < stack.accounts.length; i++) sum += stack.accounts[i]!.qty;
    return sum - (this.state.deficits.get(key2(article, location))?.qty ?? 0);
  }

  // ---- moving average (quantity articles) ----

  average(article: Id, lager: Id): Average {
    const key = key2(article, lager);
    let avg = this.state.averages.get(key);
    if (!avg) {
      avg = { qty: 0, price: null };
      this.state.averages.set(key, avg);
    }
    return avg;
  }

  addQty(article: Id, lager: Id, location: Id, qty: number, unitPrice: number | null) {
    const avg = this.average(article, lager);
    // A receipt without a price (gift, provisional transfer) leaves the average as it is.
    if (unitPrice !== null) {
      avg.price = avg.qty <= 0 || avg.price === null ? unitPrice : (avg.qty * avg.price + qty * unitPrice) / (avg.qty + qty);
    }
    avg.qty += qty;
    this.account(article, lager, location, "").qty += qty;
  }

  takeQty(article: Id, lager: Id, location: Id, qty: number): Valuation {
    const avg = this.average(article, lager);
    const acc = this.account(article, lager, location, "");
    const short = Math.max(0, qty - Math.max(acc.qty, 0));
    acc.qty -= qty;
    avg.qty -= qty;
    const val: Valuation = { value: 0, parts: [{ lot: "", qty, unitPrice: avg.price }], unpriced: 0, short };
    if (avg.price === null) val.unpriced = qty;
    else val.value = qty * avg.price;
    return val;
  }

  // ---- bookings ----

  apply(b: Booking) {
    switch (b.kind) {
      case "in": {
        if (!this.inScope(b.lager)) return;
        if (this.tracking(b.article) === "lot") {
          const lot =
            (b.lot && this.state.lots.get(b.lot)) ||
            this.addLot({
              id: b.lot ?? b.id,
              article: b.article,
              receivedAt: b.at,
              unitPrice: priceOf(b),
              ...b.lotInfo,
            });
          this.putLot(lot, b.lager, b.location, b.qty);
        } else {
          this.addQty(b.article, b.lager, b.location, b.qty, priceOf(b));
        }
        return;
      }
      case "out": {
        if (!this.inScope(b.lager)) return;
        const val =
          this.tracking(b.article) === "lot"
            ? this.takeLots(b.article, b.lager, b.location, b.qty, b.lot)
            : this.takeQty(b.article, b.lager, b.location, b.qty);
        this.state.valuations.set(b.id, val);
        return;
      }
      case "move":
        return this.move(b);
      case "convert": {
        if (!this.inScope(b.lager)) return;
        const val =
          this.tracking(b.fromArticle) === "lot"
            ? this.takeLots(b.fromArticle, b.lager, b.location, b.fromQty)
            : this.takeQty(b.fromArticle, b.lager, b.location, b.fromQty);
        this.state.valuations.set(b.id, val);
        const priced = b.fromQty - val.unpriced - val.short > 0;
        const unitPrice = priced ? val.value / b.toQty : null;
        if (this.tracking(b.toArticle) === "lot") {
          const lot = this.addLot({ id: b.id, article: b.toArticle, receivedAt: b.at, unitPrice });
          this.putLot(lot, b.lager, b.location, b.toQty);
        } else {
          this.addQty(b.toArticle, b.lager, b.location, b.toQty, unitPrice);
        }
        return;
      }
      case "count":
        return this.count(b);
      case "cancel":
        return;
    }
  }

  move(b: Extract<Booking, { kind: "move" }>) {
    const fromIn = this.inScope(b.fromLager);
    const toIn = this.inScope(b.toLager);
    if (!fromIn && !toIn) return;
    const lotArticle = this.tracking(b.article) === "lot";

    let parts: Resolution["parts"];
    if (fromIn) {
      const val = lotArticle
        ? this.takeLots(b.article, b.fromLager, b.from, b.qty, b.lot)
        : this.takeQty(b.article, b.fromLager, b.from, b.qty);
      this.state.valuations.set(b.id, val);
      parts = val.parts.map((p) => ({ lot: p.lot ? this.state.lots.get(p.lot)! : null, qty: p.qty, unitPrice: p.unitPrice }));
      // What was taken beyond the lots arrives as a provisional lot without price.
      if (lotArticle && val.short > 0) parts.push({ lot: null, qty: val.short, unitPrice: null });
    } else {
      parts = this.opts.resolutions?.get(b.id)?.parts ?? [{ lot: null, qty: b.qty, unitPrice: null }];
    }
    if (!toIn) return;

    parts.forEach((part, i) => {
      if (lotArticle && b.lotInfo) {
        // Frozen (C1): a new lot per part with the new details; price, receipt time and supplier go along.
        const lot = this.addLot({
          id: `${b.id}:${i}`,
          article: b.article,
          receivedAt: part.lot?.receivedAt ?? b.at,
          unitPrice: part.unitPrice,
          ...(part.lot?.supplier ? { supplier: part.lot.supplier } : {}),
          ...(part.lot?.batch ? { batch: part.lot.batch } : {}),
          ...b.lotInfo,
        });
        this.putLot(lot, b.toLager, b.to, part.qty);
      } else if (lotArticle) {
        const lot = part.lot
          ? this.addLot(part.lot)
          : this.addLot({ id: `move:${b.id}`, article: b.article, receivedAt: b.at, unitPrice: part.unitPrice });
        this.putLot(lot, b.toLager, b.to, part.qty);
      } else {
        this.addQty(b.article, b.toLager, b.to, part.qty, part.unitPrice);
      }
    });
  }

  count(c: Count) {
    if (!this.inScope(c.lager)) return;
    const lotArticle = this.tracking(c.article) === "lot";
    let expected: number;
    let diff: number;
    let val: Valuation;

    if (!lotArticle) {
      expected = this.state.accounts.get(key3(c.article, c.location, ""))?.qty ?? 0;
      diff = c.qty - expected;
      if (diff >= 0) {
        const price = this.average(c.article, c.lager).price;
        this.addQty(c.article, c.lager, c.location, diff, price);
        val = { value: price === null ? 0 : diff * price, parts: [{ lot: "", qty: diff, unitPrice: price }], unpriced: price === null ? diff : 0, short: 0 };
      } else {
        val = this.takeQty(c.article, c.lager, c.location, -diff);
        val.value = -val.value;
      }
    } else if (c.lot && this.countMode(c.article, c.lager) === "lot") {
      const lot = this.state.lots.get(c.lot);
      expected = this.state.accounts.get(key3(c.article, c.location, c.lot))?.qty ?? 0;
      diff = c.qty - expected;
      if (diff >= 0) {
        const known = lot ?? this.addLot({ id: c.lot, article: c.article, receivedAt: c.at, unitPrice: null });
        this.putIn(known, c.lager, c.location, diff);
        val = { value: known.unitPrice === null ? 0 : diff * known.unitPrice, parts: [{ lot: known.id, qty: diff, unitPrice: known.unitPrice }], unpriced: known.unitPrice === null ? diff : 0, short: 0 };
      } else {
        val = this.takeLots(c.article, c.lager, c.location, -diff, c.lot);
        val.value = -val.value;
      }
    } else {
      // Per article: the difference goes to the oldest lot (F8).
      expected = this.lotQty(c.article, c.location);
      diff = c.qty - expected;
      if (diff >= 0) {
        val = this.findLots(c, diff);
      } else {
        val = this.takeLots(c.article, c.lager, c.location, -diff);
        val.value = -val.value;
      }
    }

    this.state.counts.set(c.id, {
      id: c.id,
      article: c.article,
      lager: c.lager,
      location: c.location,
      ...(c.lot ? { lot: c.lot } : {}),
      ...(c.inventory ? { inventory: c.inventory } : {}),
      at: c.at,
      expected,
      counted: c.qty,
      diff,
      value: val.value,
    });
    this.state.valuations.set(c.id, val);
  }

  /** More counted than booked: settles a deficit first, then goes to the oldest lot with stock. */
  findLots(c: Count, qty: number): Valuation {
    const val: Valuation = { value: 0, parts: [], unpriced: 0, short: 0 };
    const stackKey = key2(c.article, c.location);
    const def = this.state.deficits.get(stackKey);
    if (def && def.qty > 0) {
      const settle = Math.min(def.qty, qty);
      def.qty -= settle;
      qty -= settle;
      val.unpriced += settle;
    }
    if (qty <= 0) return val;
    const stack = this.state.stacks.get(stackKey);
    const target = stack?.accounts.slice(stack.head).find((acc) => acc.qty > 0)?.lot ?? this.state.latest.get(stackKey);
    const lot = target
      ? this.state.lots.get(target)!
      : this.addLot({ id: `count:${c.id}`, article: c.article, receivedAt: c.at, unitPrice: null });
    this.putIn(lot, c.lager, c.location, qty);
    val.parts.push({ lot: lot.id, qty, unitPrice: lot.unitPrice });
    if (lot.unitPrice === null) val.unpriced += qty;
    else val.value += qty * lot.unitPrice;
    return val;
  }
}

/** Ids of bookings that count as never booked: cancelled ones and the cancels themselves. */
export function cancelledIds(bookings: Iterable<Booking>): Set<Id> {
  const out = new Set<Id>();
  const cancels: Extract<Booking, { kind: "cancel" }>[] = [];
  const cancelIds = new Set<Id>();
  for (const b of bookings) {
    if (b.kind !== "cancel") continue;
    cancels.push(b);
    cancelIds.add(b.id);
  }
  for (const c of cancels) {
    // A cancel of a cancel is ignored: "undo the undo" means booking again.
    if (cancelIds.has(c.cancels)) continue;
    out.add(c.cancels);
    out.add(c.id);
  }
  return out;
}

/**
 * Computes stock, lots, averages, count differences and valuations from the journal.
 * With `from`, only bookings after the snapshot's cutoff count; earlier ones are in the snapshot.
 */
export function computeState(bookings: Booking[], opts: ComputeOptions = {}): State {
  const ledger = new Ledger(opts);
  const cutoff = opts.from?.cutoff ?? -Infinity;
  const list = opts.sorted ? bookings : [...bookings].sort(compareBookings);
  const cancelled = cancelledIds(list);
  ledger.state.cancelled = cancelled;
  for (const b of list) {
    if (b.at <= cutoff) continue;
    ledger.state.last = b;
    if (!cancelled.has(b.id)) ledger.apply(b);
  }
  return ledger.state;
}

/**
 * Appends bookings that all come after the last processed one, without computing again (the usual
 * case: a booking made just now). Changes `state` in place and returns true; returns false and
 * leaves `state` untouched when that is not possible (a late booking or a cancel): compute anew then.
 */
export function extendState(state: State, bookings: Booking[], opts: ComputeOptions = {}): boolean {
  const list = opts.sorted ? bookings : [...bookings].sort(compareBookings);
  let last = state.last;
  for (const b of list) {
    if (b.kind === "cancel" || (last && compareBookings(last, b) >= 0) || (state.cutoff !== null && b.at <= state.cutoff)) return false;
    last = b;
  }
  const ledger = new Ledger(opts, state);
  for (const b of list) {
    // A cancel already processed may name a booking that sorts after it (same time, larger id).
    if (!state.cancelled.has(b.id)) ledger.apply(b);
    state.last = b;
  }
  return true;
}

// ---- reading the state ----

export interface StockLine {
  article: Id;
  lager: Id;
  location: Id;
  lot: Id;
  qty: number;
  /** Value in cents (unknown prices count 0). */
  value: number;
  unpriced: boolean;
}

/** All stock lines with a quantity (deficits as negative lines without lot). */
export function stockLines(state: State): StockLine[] {
  const out: StockLine[] = [];
  for (const acc of state.accounts.values()) {
    if (acc.qty === 0) continue;
    let price: number | null;
    if (acc.lot) price = state.lots.get(acc.lot)?.unitPrice ?? null;
    else price = state.averages.get(key2(acc.article, acc.lager))?.price ?? null;
    out.push({ ...acc, value: price === null ? 0 : acc.qty * price, unpriced: price === null });
  }
  for (const d of state.deficits.values()) {
    if (d.qty > 0) out.push({ article: d.article, lager: d.lager, location: d.location, lot: "", qty: -d.qty, value: 0, unpriced: true });
  }
  return out;
}

/** Stock lines as sorted strings with rounded values, for comparing devices and server. */
export function normalizedLines(lines: StockLine[]): string[] {
  return lines
    .map((l) => `${l.article}|${l.lager}|${l.location}|${l.lot}|${l.qty}|${Math.round(l.value * 1e6) / 1e6}|${l.unpriced}`)
    .sort();
}

export interface ArticleStock {
  article: Id;
  lager: Id;
  qty: number;
  value: number;
}

/** Quantity and value per article and storage. */
export function stockByArticle(state: State): ArticleStock[] {
  const map = new Map<string, ArticleStock>();
  for (const line of stockLines(state)) {
    const key = key2(line.article, line.lager);
    const sum = map.get(key) ?? { article: line.article, lager: line.lager, qty: 0, value: 0 };
    sum.qty += line.qty;
    sum.value += line.value;
    map.set(key, sum);
  }
  return [...map.values()];
}

/** Quantity of one article at one location (all lots, minus a deficit). */
export function quantityAt(state: State, article: Id, location: Id): number {
  let sum = 0;
  for (const acc of state.accounts.values()) {
    if (acc.article === article && acc.location === location) sum += acc.qty;
  }
  return sum - (state.deficits.get(key2(article, location))?.qty ?? 0);
}

// ---- snapshots ----

/** The state as a snapshot at a cutoff (the state must contain exactly the bookings up to it). */
export function toSnapshot(state: State, cutoff: Millis): Snapshot {
  const lotsUsed = new Set<Id>();
  const accounts: Account[] = [];
  for (const acc of state.accounts.values()) {
    // Empty lot accounts stay: they keep a lot's place for "oldest first" and the latest lot.
    if (acc.qty === 0 && !acc.lot) continue;
    accounts.push({ ...acc });
    if (acc.lot) lotsUsed.add(acc.lot);
  }
  const latest: Snapshot["latest"] = [];
  for (const [key, lot] of state.latest) {
    const [article, location] = key.split("|") as [Id, Id];
    latest.push({ article, location, lot });
    lotsUsed.add(lot);
  }
  return {
    cutoff,
    accounts,
    lots: [...lotsUsed].map((id) => ({ ...state.lots.get(id)! })),
    deficits: [...state.deficits.values()].filter((d) => d.qty !== 0).map((d) => ({ ...d })),
    averages: [...state.averages.entries()].map(([key, avg]) => {
      const [article, lager] = key.split("|") as [Id, Id];
      return { article, lager, qty: avg.qty, price: avg.price };
    }),
    latest,
  };
}

/** Only the storages a device holds (partial sync). */
export function filterSnapshot(snapshot: Snapshot, lagers: ReadonlySet<Id>): Snapshot {
  const accounts = snapshot.accounts.filter((a) => lagers.has(a.lager));
  const locations = new Set(accounts.map((a) => a.location));
  const deficits = snapshot.deficits.filter((d) => lagers.has(d.lager));
  for (const d of deficits) locations.add(d.location);
  const latest = snapshot.latest.filter((l) => locations.has(l.location));
  const lotIds = new Set([...accounts.map((a) => a.lot), ...latest.map((l) => l.lot)]);
  return {
    cutoff: snapshot.cutoff,
    accounts,
    lots: snapshot.lots.filter((l) => lotIds.has(l.id)),
    deficits,
    averages: snapshot.averages.filter((a) => lagers.has(a.lager)),
    latest,
  };
}

function fromSnapshot(s: Snapshot): State {
  const state = emptyState();
  state.cutoff = s.cutoff;
  for (const lot of s.lots) state.lots.set(lot.id, { ...lot });
  for (const snap of s.accounts) {
    const acc = { ...snap };
    state.accounts.set(key3(acc.article, acc.location, acc.lot), acc);
    if (!acc.lot) continue;
    const stackKey = key2(acc.article, acc.location);
    const stack = state.stacks.get(stackKey) ?? { accounts: [], head: 0 };
    stack.accounts.push(acc);
    state.stacks.set(stackKey, stack);
  }
  for (const stack of state.stacks.values()) {
    stack.accounts.sort((a, b) => (olderLot(state.lots.get(a.lot)!, state.lots.get(b.lot)!) ? -1 : 1));
  }
  for (const d of s.deficits) state.deficits.set(key2(d.article, d.location), { ...d });
  for (const a of s.averages) state.averages.set(key2(a.article, a.lager), { qty: a.qty, price: a.price });
  for (const l of s.latest) state.latest.set(key2(l.article, l.location), l.lot);
  return state;
}

/**
 * Resolutions for transfers between two storages, for devices that hold the target but not the
 * source (computed from the full journal on the server).
 */
export function resolutionsFor(state: State, transfers: Iterable<Booking>): Map<Id, Resolution> {
  const out = new Map<Id, Resolution>();
  for (const b of transfers) {
    if (b.kind !== "move" || b.fromLager === b.toLager || state.cancelled.has(b.id)) continue;
    const val = state.valuations.get(b.id);
    if (!val) continue;
    const parts: Resolution["parts"] = val.parts.map((p) => ({
      lot: p.lot ? state.lots.get(p.lot)! : null,
      qty: p.qty,
      unitPrice: p.unitPrice,
    }));
    if (val.short > 0 && !isQuantityValuation(val)) parts.push({ lot: null, qty: val.short, unitPrice: null });
    out.set(b.id, { parts });
  }
  return out;
}
